// Cloned and modified from components/scalers/tasks/ThreadedInteraction/definitions/TextCollection.tsx
import _ from 'lodash';
import { TextCollectionCore } from 'react-scale/components/scalers/tasks/textcollection/TextCollectionCore';
import { Button } from '@scale/scaleui';
import { InteractionStepTaskComponent } from '../../InteractionStep';
import { TextCollectionResponse } from '@scale/shared/types/response/textcollection';
import { useContext, useMemo, useState, useRef, memo } from 'react';
import {
  DEFAULT_TEXTCOLLECTION_SUBTITLE,
  DEFAULT_TEXTCOLLECTION_TITLE,
  InteractionStepType,
  QIRTextCollectionStepResponse,
} from '@scale/shared/types/chat';
import { useTrans } from '../../hooks/useTrans';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import useLocalStorageWithExpiry, {
  getLocalStorageKey,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useLocalStorageWithExpiry';
import {
  useChatStoreExtract,
  useMostRecentResponse,
  useStepParams,
  useStepResponse,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { useStepSubmitHandler } from '../../hooks/useStepSubmitHandler';
import { StepWidthWrapper } from '../../step/StepWidthWrapper';
import { PinButton } from '../../step/PinButton';
import { StepCard } from '../../step/StepCard';

const QIRTextCollectionTaskComponent: InteractionStepTaskComponent = ({
  readOnly,
  responseIndex,
  complete,
}) => {
  const taskContext = useContext(ChatTaskContext);
  const _step = useStepParams<InteractionStepType.TextCollection>(responseIndex);

  const response = useStepResponse<QIRTextCollectionStepResponse>(responseIndex);

  const lastResponse = useMostRecentResponse(responseIndex);

  const step = useMemo(() => {
    const patchedStep = _.cloneDeep(_step);
    patchedStep.params.fields = patchedStep.params.fields.map(field => ({
      ...field,
      disable_pasting:
        (taskContext.disablePastingTextCollectionFieldIds ?? []).includes(field.field_id) ||
        taskContext.disablePastingAllTextCollectionFields,
    }));
    return patchedStep;
  }, [
    _step,
    taskContext.disablePastingTextCollectionFieldIds,
    taskContext.disablePastingAllTextCollectionFields,
  ]);

  const { onSubmit } = useStepSubmitHandler({
    responseIndex,
  });

  const { t } = useTrans();
  const [lastSavedResponse, setLastSavedResponse] = useState<TextCollectionResponse>(
    response
      ? _.cloneDeep(response.context.response)
      : {
          annotations: {},
        },
  );
  const localSaveKey = getLocalStorageKey({
    assignmentId: taskContext.assignmentId,
    stepType: _step.type,
    responseIndex,
    disabled: taskContext.isPipelineV2,
  });

  const [currentResponse, setCurrentResponse, clearBackup] =
    useLocalStorageWithExpiry<TextCollectionResponse>(lastSavedResponse, localSaveKey);

  const cardRef = useRef<HTMLDivElement>(null);

  const markStepAsIncompletedByIndex = useChatStoreExtract(
    state => state.markStepAsIncompletedByIndex,
  );
  const markStepAsCompletedByIndex = useChatStoreExtract(state => state.markStepAsCompletedByIndex);
  const canUpdateCompletedState = response && !_.isNil(responseIndex);

  return (
    <StepWidthWrapper>
      <StepCard readOnly={readOnly}>
        <StepCard.Header>
          <StepCard.HeaderLeft>
            <StepCard.Icon icon="question-circle" />
            <StepCard.HeaderColumn>
              <StepCard.Title>{step.title || DEFAULT_TEXTCOLLECTION_TITLE}</StepCard.Title>
              <StepCard.Subtitle>
                {step.subtitle || DEFAULT_TEXTCOLLECTION_SUBTITLE}
              </StepCard.Subtitle>
            </StepCard.HeaderColumn>
          </StepCard.HeaderLeft>
          <StepCard.HeaderRight>
            <PinButton responseIndex={responseIndex} />
          </StepCard.HeaderRight>
        </StepCard.Header>
        <StepCard.Body>
          <div ref={cardRef} className="flex justify-center w-full p-4 my-2 rounded-md">
            <div className="relative max-[800px]:w-72 min-[800px]:w-[90%]">
              <TextCollectionCore
                params={{ ...step.params, attachments: [] }}
                response={currentResponse}
                setResponse={newResponse => {
                  setCurrentResponse(newResponse);
                  if (
                    canUpdateCompletedState &&
                    !_.isEqual(newResponse, lastSavedResponse) &&
                    !readOnly &&
                    complete
                  ) {
                    markStepAsIncompletedByIndex(responseIndex);
                  } else if (
                    // Explicitly mark the step as completed if the current response is equal to the last saved response
                    canUpdateCompletedState &&
                    _.isEqual(newResponse, lastSavedResponse) &&
                    !readOnly &&
                    !complete
                  ) {
                    markStepAsCompletedByIndex(responseIndex);
                  }
                }}
                readOnly={readOnly}
                showSidebarResponsive={true}
                fullWidthSidebar={true}
                chatTaskBehaviorLogMetadata={{
                  responseIndex,
                }}
                assignmentId={taskContext.assignmentId}
              />
            </div>
          </div>
        </StepCard.Body>
        {!taskContext.previewMode && (
          <StepCard.Footer className="flex gap-2 justify-end">
            <Button
              onClick={async () => {
                const isValid = await onSubmit(
                  {
                    type: InteractionStepType.QIRTextCollection,
                    context: {
                      response: currentResponse,
                    },
                    output: lastResponse,
                  },
                  responseIndex,
                  _step,
                );
                if (isValid) {
                  setLastSavedResponse(currentResponse);
                  clearBackup();
                }
              }}
              fullWidth
              disabled={readOnly || _.isEqual(currentResponse, lastSavedResponse)}
            >
              {t('save-and-continue')}
            </Button>
          </StepCard.Footer>
        )}
      </StepCard>
    </StepWidthWrapper>
  );
};

export default memo(QIRTextCollectionTaskComponent);
